<template>
  <div class="page-content">
    <div class="title">AI models</div>
    <div class="ai-settings">
      <p class="ai-settings__intro">
        The Claude model and effort each AI feature of the admin uses, for this
        store. The list of models comes from Anthropic, so new ones show up here
        on their own. Changes apply to the next request.
      </p>

      <section class="ai-settings__card">
        <h3>Anthropic API key</h3>
        <p v-if="apiKey">
          <template v-if="apiKey.source === 'settings'">
            Using the key saved here, ending in
            <code>…{{ apiKey.last4 }}</code
            >.
          </template>
          <template v-else-if="apiKey.source === 'env'">
            Using the server key (environment variable), ending in
            <code>…{{ apiKey.last4 }}</code
            >.
          </template>
          <template v-else>
            <span class="ai-settings__error">No key configured.</span>
          </template>
        </p>
        <template v-if="isSuperadmin">
          <div class="ai-settings__key-form">
            <input
              v-model="newKey"
              type="password"
              autocomplete="off"
              placeholder="sk-ant-…"
            />
            <a
              class="btn btn-success-button"
              :class="{ disabled: !newKey.trim() || savingKey }"
              @click="saveKey"
            >
              {{ savingKey ? "Checking…" : "Save key" }}
            </a>
            <a
              v-if="apiKey && apiKey.source === 'settings'"
              class="ai-settings__link"
              @click="clearKey"
            >
              Use the server key
            </a>
          </div>
          <small>
            The key is checked against Anthropic before saving and is never
            shown again: only its last 4 characters.
          </small>
        </template>
        <small v-else>Only a superadmin can change the key.</small>
      </section>

      <p v-if="loading">Loading models…</p>
      <p v-else-if="error" class="ai-settings__error">{{ error }}</p>
      <template v-else>
        <p v-if="modelsError" class="ai-settings__error">
          Could not load the models from Anthropic: {{ modelsError }}
        </p>

        <section class="ai-settings__card">
          <h3>Model per feature</h3>
          <div
            v-for="feature in features"
            :key="feature.key"
            class="ai-feature"
          >
            <div class="ai-feature__info">
              <strong>{{ feature.label }}</strong>
              <small>{{ feature.description }}</small>
              <small v-if="isDefault(feature)" class="ai-feature__default">
                Default
              </small>
              <a
                v-else
                class="ai-settings__link"
                @click="resetFeature(feature)"
              >
                Back to default ({{ feature.defaultModel
                }}{{
                  feature.defaultEffort ? `, ${feature.defaultEffort}` : ""
                }})
              </a>
            </div>
            <select
              v-model="feature.model"
              class="default-select"
              @change="onModelChange(feature)"
            >
              <!-- El que está configurado aunque la key ya no lo vea -->
              <option v-if="!modelById(feature.model)" :value="feature.model">
                {{ feature.model }}
              </option>
              <option
                v-for="model in models"
                :key="model.id"
                :value="model.id"
                :disabled="!!missingFor(feature, model)"
              >
                {{ model.displayName
                }}{{
                  missingFor(feature, model)
                    ? ` — ${missingFor(feature, model)}`
                    : ""
                }}
              </option>
            </select>
            <select
              v-model="feature.effort"
              class="default-select"
              :disabled="!effortsOf(feature).length"
            >
              <option value="">
                {{ effortsOf(feature).length ? "Model default" : "No effort" }}
              </option>
              <option
                v-for="level in effortsOf(feature)"
                :key="level"
                :value="level"
              >
                {{ level }}
              </option>
            </select>
          </div>
          <small>
            Higher effort thinks more: better answers, slower and more
            expensive. Simple high-volume tasks (categorizing, mapping, resale
            tax) usually do well with a small model.
          </small>
        </section>

        <div class="ai-settings__actions">
          <a
            class="btn btn-success-button"
            :class="{ disabled: !changed.length || saving }"
            @click="save"
          >
            {{
              saving
                ? "Saving…"
                : `Save${changed.length ? ` (${changed.length})` : ""}`
            }}
          </a>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  getAiModels,
  saveAnthropicApiKey,
  clearAnthropicApiKey
} from "@/api/store/AiService.js";
import { updateSetting } from "@/api/store/SettingsService.js";

// Lo que una función pide al modelo (requires en ai_models_helper.php)
const REQUIREMENTS = {
  tools: { check: model => model.supportsTools, missing: "no strict tools" },
  images: { check: model => model.supportsImages, missing: "no image input" }
};

// Store Settings > AI models: settings ai_<función>_model y
// ai_<función>_effort de la tienda, que lee ai_models_helper.php del API en
// cada llamada a Claude; y la API key de Anthropic (sólo superadmin)
export default {
  name: "AiModelsSettings",
  data() {
    return {
      models: [],
      modelsError: null,
      features: [],
      saved: {},
      apiKey: null,
      newKey: "",
      savingKey: false,
      loading: true,
      saving: false,
      error: null
    };
  },
  computed: {
    isSuperadmin() {
      return this.$store.getters["auth/isSuperadmin"];
    },
    changed() {
      return this.features.filter(feature => {
        const saved = this.saved[feature.key];
        return feature.model !== saved.model || feature.effort !== saved.effort;
      });
    }
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const data = await getAiModels();
        // Los más nuevos primero
        this.models = data.models
          .slice()
          .sort((a, b) =>
            String(b.createdAt).localeCompare(String(a.createdAt))
          );
        this.modelsError = data.modelsError;
        this.apiKey = data.apiKey;
        this.features = data.features;
        this.saved = this.features.reduce((acc, feature) => {
          acc[feature.key] = { model: feature.model, effort: feature.effort };
          return acc;
        }, {});
      } catch (e) {
        this.error =
          e?.response?.data?.message || e?.message || "Could not load.";
      } finally {
        this.loading = false;
      }
    },
    modelById(id) {
      return this.models.find(model => model.id === id) || null;
    },
    missingFor(feature, model) {
      const requirement = REQUIREMENTS[feature.requires];
      return requirement && !requirement.check(model)
        ? requirement.missing
        : null;
    },
    effortsOf(feature) {
      const model = this.modelById(feature.model);
      return model ? model.efforts : [];
    },
    isDefault(feature) {
      return (
        feature.model === feature.defaultModel &&
        feature.effort === feature.defaultEffort
      );
    },
    // Un effort que el modelo nuevo no acepta vuelve al default del modelo
    onModelChange(feature) {
      if (feature.effort && !this.effortsOf(feature).includes(feature.effort)) {
        feature.effort = "";
      }
    },
    resetFeature(feature) {
      feature.model = feature.defaultModel;
      feature.effort = feature.defaultEffort;
    },
    async save() {
      if (!this.changed.length || this.saving) return;
      this.saving = true;
      const results = await Promise.all(
        this.changed.flatMap(feature => [
          updateSetting(`ai_${feature.key}_model`, feature.model, 1),
          updateSetting(`ai_${feature.key}_effort`, feature.effort, 1)
        ])
      );
      this.saving = false;
      if (results.every(result => result && result.status === "success")) {
        this.changed.forEach(feature => {
          this.saved[feature.key] = {
            model: feature.model,
            effort: feature.effort
          };
        });
        this.toast("AI models saved", "success");
      } else {
        this.toast("Could not save some AI models", "error");
      }
    },
    async saveKey() {
      if (!this.newKey.trim() || this.savingKey) return;
      this.savingKey = true;
      try {
        const { apiKey } = await saveAnthropicApiKey(this.newKey.trim());
        this.newKey = "";
        this.toast("Anthropic API key saved", "success");
        this.apiKey = apiKey;
        // Otra key puede ver otros modelos
        this.load();
      } catch (e) {
        this.toast(
          e?.response?.data?.message || "Could not save the key",
          "error"
        );
      } finally {
        this.savingKey = false;
      }
    },
    async clearKey() {
      try {
        const { apiKey } = await clearAnthropicApiKey();
        this.apiKey = apiKey;
        this.toast("Using the server key again", "success");
        this.load();
      } catch (e) {
        this.toast(
          e?.response?.data?.message || "Could not remove the key",
          "error"
        );
      }
    },
    toast(message, type) {
      this.$toast.open({ message, type, position: "top-right" });
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-settings {
  max-width: 960px;
  margin: 0 auto;
}
.ai-settings__intro {
  margin-bottom: 16px;
  color: #4b5563;
}
.ai-settings__card {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  h3 {
    margin-bottom: 8px;
    font-weight: 600;
  }
  small {
    display: block;
    color: #6b7280;
  }
  code {
    padding: 0 4px;
    background: #f3f4f6;
    border-radius: 4px;
  }
}
.ai-settings__key-form {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 4px;
  input {
    flex: 1;
    max-width: 420px;
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
  }
}
.ai-feature {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px 150px;
  gap: 12px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
  select {
    width: 100%;
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}
.ai-feature__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.ai-feature__default {
  color: #16a34a !important;
}
.ai-settings__link {
  font-size: 12px;
  color: #2563eb;
  cursor: pointer;
}
.ai-settings__actions {
  display: flex;
  justify-content: flex-end;
  .disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}
.btn.disabled {
  opacity: 0.5;
  pointer-events: none;
}
.ai-settings__error {
  color: #b91c1c;
}
</style>
